import { useEffect, useMemo, useState } from 'react';
import { Coins, Layers, Sprout, TrendingUp } from 'lucide-react';
import { formatUnits, parseUnits } from 'viem';
import {
  useAccount,
  usePublicClient,
  useReadContract,
  useReadContracts,
  useWaitForTransactionReceipt,
  useWriteContract,
} from 'wagmi';
import { MARKET_PAIRS, type MarketPair } from '../data/mockMarket';
import { ERC20_ABI, FARM_ABI } from '../web3/abis';
import { TARGET_CHAIN } from '../web3/config';
import { getDeployment, getFarmPid, getPairAddress } from '../web3/contracts';
import { logTransaction } from '../web3/logTransaction';
import { useTokenPrices } from '../web3/usePrices';
import TokenIcon from './TokenIcon';
import TrustBar from './TrustBar';
import TransactionTimeout from './TransactionTimeout';

const DECIMALS = 18;
const BLOCKS_PER_YEAR = 10_512_000; // BSC ~3s blocks
const toNum = (v: bigint) => Number(formatUnits(v, DECIMALS));

function fmt(value: bigint | undefined, digits = 4) {
  if (value === undefined) return '-';
  return Number(formatUnits(value, DECIMALS)).toLocaleString('en-US', { maximumFractionDigits: digits });
}

function fmtUsd(value: number | undefined) {
  if (value === undefined) return '-';
  return `$${value.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
}

// testnet pools carry dust liquidity, so raw emission APR can explode into the
// millions; clamp the display to a believable ceiling for a financial UI
function fmtApr(apr: number | undefined) {
  if (apr === undefined) return '-';
  if (apr > 999) return '999%+';
  return `${apr.toFixed(1)}%`;
}

interface FarmStats {
  ready: boolean;
  dailyPlg: number;
  totalTvl: number;
  aprOf: (poolId: string) => number | undefined;
  tvlOf: (poolId: string) => number | undefined;
}

// Reads farm-wide emission + per-pool alloc/staked, combines with pool prices
// to derive each pool's TVL and fee-free reward APR.
function useFarmStats(): FarmStats {
  const { chainId } = useAccount();
  const readChainId = chainId ?? TARGET_CHAIN.id;
  const deployment = getDeployment(readChainId);
  const farm = deployment?.farm;
  const prices = useTokenPrices();

  const pools = useMemo(
    () =>
      MARKET_PAIRS.map((p) => ({ id: p.id, pid: getFarmPid(p.id, readChainId), pair: getPairAddress(p.id, readChainId) }))
        .filter((p) => p.pid !== undefined && p.pair),
    [readChainId],
  );

  const contracts = useMemo(() => {
    if (!farm) return [];
    const base = [
      { address: farm, abi: FARM_ABI, functionName: 'rewardPerBlock', chainId: readChainId } as const,
      { address: farm, abi: FARM_ABI, functionName: 'totalAllocPoint', chainId: readChainId } as const,
    ];
    const perPool = pools.flatMap((p) => [
      { address: farm, abi: FARM_ABI, functionName: 'poolInfo', args: [BigInt(p.pid!)], chainId: readChainId } as const,
      { address: p.pair, abi: ERC20_ABI, functionName: 'balanceOf', args: [farm], chainId: readChainId } as const,
    ]);
    return [...base, ...perPool];
  }, [farm, pools, readChainId]);

  const { data } = useReadContracts({
    contracts,
    query: { enabled: contracts.length > 0, refetchInterval: 10000 },
  });

  return useMemo(() => {
    const empty: FarmStats = { ready: false, dailyPlg: 0, totalTvl: 0, aprOf: () => undefined, tvlOf: () => undefined };
    if (!data || !prices.ready || data[0]?.status !== 'success' || data[1]?.status !== 'success') return empty;

    const rewardPerBlock = toNum(data[0].result as bigint);
    const totalAlloc = Number(data[1].result as bigint);
    const plgPrice = prices.priceOf('PLG') ?? 0;
    const dailyPlg = rewardPerBlock * (BLOCKS_PER_YEAR / 365);

    const aprMap = new Map<string, number>();
    const tvlMap = new Map<string, number>();
    let totalTvl = 0;

    pools.forEach((p, i) => {
      const info = data[2 + i * 2];
      const bal = data[2 + i * 2 + 1];
      if (info?.status !== 'success' || bal?.status !== 'success') return;
      const allocPoint = Number((info.result as readonly [string, bigint, bigint, bigint])[1]);
      const farmLp = toNum(bal.result as bigint);
      const lpPrice = prices.lpPriceOf(p.id) ?? 0;
      const tvl = farmLp * lpPrice;
      tvlMap.set(p.id, tvl);
      totalTvl += tvl;
      if (totalAlloc > 0 && tvl > 0) {
        const annualPlg = rewardPerBlock * BLOCKS_PER_YEAR * (allocPoint / totalAlloc);
        aprMap.set(p.id, ((annualPlg * plgPrice) / tvl) * 100);
      }
    });

    return {
      ready: true,
      dailyPlg,
      totalTvl,
      aprOf: (id: string) => aprMap.get(id),
      tvlOf: (id: string) => tvlMap.get(id),
    };
  }, [data, prices, pools]);
}

function safeParse(value: string): bigint | null {
  if (!value || Number.isNaN(Number(value)) || Number(value) <= 0) return null;
  try {
    return parseUnits(value as `${number}`, DECIMALS);
  } catch {
    return null;
  }
}

function useFarmPoolData(pool: MarketPair) {
  const { address, chainId } = useAccount();
  const readChainId = chainId ?? TARGET_CHAIN.id;
  const deployment = getDeployment(readChainId);
  const pairAddress = getPairAddress(pool.id, readChainId);
  const pid = getFarmPid(pool.id, readChainId);

  const { data: walletLpBalance, refetch: refetchWalletLp } = useReadContract({
    address: pairAddress,
    abi: ERC20_ABI,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(pairAddress && address) },
  });

  const { data: allowance, refetch: refetchAllowance } = useReadContract({
    address: pairAddress,
    abi: ERC20_ABI,
    functionName: 'allowance',
    args: address && deployment?.farm ? [address, deployment.farm] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(pairAddress && address && deployment?.farm) },
  });

  const { data: userInfo, refetch: refetchUserInfo } = useReadContract({
    address: deployment?.farm,
    abi: FARM_ABI,
    functionName: 'userInfo',
    args: pid !== undefined && address ? [BigInt(pid), address] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(deployment?.farm && pid !== undefined && address) },
  });

  const { data: pendingReward, refetch: refetchPending } = useReadContract({
    address: deployment?.farm,
    abi: FARM_ABI,
    functionName: 'pendingReward',
    args: pid !== undefined && address ? [BigInt(pid), address] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(deployment?.farm && pid !== undefined && address), refetchInterval: 5000 },
  });

  const stakedAmount = userInfo?.[0];

  const refetchAll = () => {
    refetchWalletLp();
    refetchAllowance();
    refetchUserInfo();
    refetchPending();
  };

  return {
    readChainId,
    deployment,
    pairAddress,
    pid,
    walletLpBalance,
    allowance,
    stakedAmount,
    pendingReward,
    refetchAll,
  };
}

interface PendingFarmReward {
  pool: MarketPair;
  pid: number;
  reward: bigint;
}

function useFarmPendingRewards() {
  const { address, chainId } = useAccount();
  const readChainId = chainId ?? TARGET_CHAIN.id;
  const farm = getDeployment(readChainId)?.farm;
  const pools = useMemo(
    () =>
      MARKET_PAIRS.flatMap((pool) => {
        const pid = getFarmPid(pool.id, readChainId);
        return pid === undefined ? [] : [{ pool, pid }];
      }),
    [readChainId],
  );

  const contracts = useMemo(() => {
    if (!farm || !address) return [];
    return pools.map(
      ({ pid }) =>
        ({
          address: farm,
          abi: FARM_ABI,
          functionName: 'pendingReward',
          args: [BigInt(pid), address],
          chainId: readChainId,
        }) as const,
    );
  }, [address, farm, pools, readChainId]);

  const { data, isPending, refetch } = useReadContracts({
    contracts,
    query: { enabled: contracts.length > 0, refetchInterval: 5000 },
  });

  const rewards = useMemo<PendingFarmReward[]>(() => {
    if (!data) return [];
    return pools.flatMap(({ pool, pid }, index) => {
      const result = data[index];
      return result?.status === 'success' ? [{ pool, pid, reward: result.result as bigint }] : [];
    });
  }, [data, pools]);

  const ready = Boolean(
    address &&
      farm &&
      contracts.length > 0 &&
      data?.length === contracts.length &&
      data.every((result) => result.status === 'success'),
  );
  const pendingPools = rewards.filter(({ reward }) => reward > 0n);
  const totalPending = pendingPools.reduce((sum, { reward }) => sum + reward, 0n);

  return { farm, readChainId, ready, isPending, pendingPools, totalPending, refetch };
}

export default function FarmView() {
  const [selected, setSelected] = useState<MarketPair>(MARKET_PAIRS[0]);
  const { chainId } = useAccount();
  const deployment = getDeployment(chainId ?? TARGET_CHAIN.id);
  const stats = useFarmStats();
  const pendingRewards = useFarmPendingRewards();

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-y-auto">
      <div className="p-4 border-b border-abyss-700 space-y-3">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <FarmStat icon={Sprout} label="Farm 전체 TVL" value={fmtUsd(stats.ready ? stats.totalTvl : undefined)} />
          <FarmStat
            icon={Coins}
            label="일일 PLG 배출"
            value={stats.ready ? `${stats.dailyPlg.toLocaleString('en-US', { maximumFractionDigits: 0 })} PLG` : '-'}
          />
          <FarmStat icon={Layers} label="활성 파밍 풀" value={String(Object.keys(deployment?.farmPools ?? {}).length)} />
          <FarmStat
            icon={TrendingUp}
            label="최고 APR"
            value={
              stats.ready
                ? (() => {
                    const aprs = MARKET_PAIRS.map((p) => stats.aprOf(p.id)).filter((a): a is number => a !== undefined);
                    return aprs.length ? fmtApr(Math.max(...aprs)) : '-';
                  })()
                : '-'
            }
            accent
          />
        </div>
        <HarvestAllPanel rewards={pendingRewards} />
      </div>

      <div className="flex flex-col lg:flex-row flex-1 min-h-0">
        <div className="w-full lg:w-[28rem] border-r border-abyss-700 shrink-0">
          <div className="p-4 border-b border-abyss-700">
            <h2 className="text-sm font-semibold text-foam-100">Farms</h2>
            <p className="text-xs text-foam-500 mt-1">LP 토큰을 스테이킹하고 블록마다 PLG 보상을 받으세요.</p>
          </div>
          <div className="divide-y divide-abyss-700">
            {MARKET_PAIRS.map((pool) => (
              <FarmRow
                key={pool.id}
                pool={pool}
                stats={stats}
                isSelected={pool.id === selected.id}
                onSelect={() => setSelected(pool)}
              />
            ))}
          </div>
        </div>

        <div className="flex-1 p-6 space-y-6 max-w-xl">
          <StakePanel pool={selected} stats={stats} />
          <TrustBar contract={deployment?.farm} badges={['nonCustodial', 'principalSafe', 'onchain']} />
        </div>
      </div>
    </div>
  );
}

type HarvestAllProgress =
  | { phase: 'idle' }
  | { phase: 'signing' | 'confirming'; current: number; total: number; poolId: string; completed: number }
  | { phase: 'success'; completed: number }
  | { phase: 'error'; completed: number; total: number; message: string };

function HarvestAllPanel({ rewards }: { rewards: ReturnType<typeof useFarmPendingRewards> }) {
  const { address, isConnected, chainId } = useAccount();
  const publicClient = usePublicClient({ chainId: rewards.readChainId });
  const { writeContractAsync } = useWriteContract();
  const [progress, setProgress] = useState<HarvestAllProgress>({ phase: 'idle' });

  const isBusy = progress.phase === 'signing' || progress.phase === 'confirming';
  const onCorrectChain = chainId === TARGET_CHAIN.id;

  const handleHarvestAll = async () => {
    const targets = rewards.pendingPools;
    if (!address || !rewards.farm || !publicClient || targets.length === 0 || isBusy) return;

    let completed = 0;
    try {
      for (let index = 0; index < targets.length; index += 1) {
        const target = targets[index];
        setProgress({ phase: 'signing', current: index + 1, total: targets.length, poolId: target.pool.id, completed });
        const hash = await writeContractAsync({
          address: rewards.farm,
          abi: FARM_ABI,
          functionName: 'deposit',
          args: [BigInt(target.pid), 0n],
        });

        setProgress({ phase: 'confirming', current: index + 1, total: targets.length, poolId: target.pool.id, completed });
        const receipt = await publicClient.waitForTransactionReceipt({ hash, timeout: 300_000 });
        if (receipt.status !== 'success') throw new Error(`${target.pool.id} 수확 트랜잭션이 되돌려졌습니다.`);

        logTransaction({
          type: 'harvest',
          hash,
          account: address,
          chainId: rewards.readChainId,
          details: { pool: target.pool.id, harvestAll: 'true' },
        });
        completed += 1;
      }

      await rewards.refetch();
      setProgress({ phase: 'success', completed });
    } catch (error) {
      await rewards.refetch();
      setProgress({
        phase: 'error',
        completed,
        total: targets.length,
        message: error instanceof Error ? error.message : '전체 수확 중 알 수 없는 오류가 발생했습니다.',
      });
    }
  };

  let buttonLabel = '전체 수확';
  if (!isConnected) buttonLabel = '지갑 연결 필요';
  else if (!onCorrectChain) buttonLabel = '네트워크 전환 필요';
  else if (!rewards.ready || rewards.isPending) buttonLabel = '보상 확인 중';
  else if (rewards.pendingPools.length === 0) buttonLabel = '수확할 보상 없음';
  else if (progress.phase === 'signing') buttonLabel = `${progress.current}/${progress.total} 지갑 승인 대기`;
  else if (progress.phase === 'confirming') buttonLabel = `${progress.current}/${progress.total} 확인 중`;

  const disabled =
    !isConnected ||
    !onCorrectChain ||
    !rewards.ready ||
    rewards.isPending ||
    rewards.pendingPools.length === 0 ||
    isBusy;

  return (
    <div className="rounded-xl border border-tide-400/30 bg-tide-400/5 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
      <div>
        <div className="text-xs text-foam-500">전체 미수령 PLG 보상</div>
        <div className="flex items-baseline gap-2 mt-0.5">
          <span className="text-xl font-semibold text-tide-400 tabular-nums">
            {rewards.ready ? fmt(rewards.totalPending, 6) : '-'}
          </span>
          <span className="text-xs text-foam-500">
            {rewards.ready ? `${rewards.pendingPools.length}개 풀` : '확인 중'}
          </span>
        </div>
        {progress.phase === 'signing' && (
          <div className="text-[11px] text-sky-400 mt-1">{progress.poolId}: 지갑에서 트랜잭션을 승인하세요.</div>
        )}
        {progress.phase === 'confirming' && (
          <div className="text-[11px] text-sky-400 mt-1">{progress.poolId}: 블록 확인 중입니다.</div>
        )}
        {progress.phase === 'success' && (
          <div className="text-[11px] text-tide-400 mt-1">{progress.completed}개 풀의 보상을 모두 수확했습니다.</div>
        )}
        {progress.phase === 'error' && (
          <div className="text-[11px] text-coral-400 mt-1 break-words">
            {progress.completed}/{progress.total}개 완료 후 중단: {progress.message}
          </div>
        )}
      </div>
      <button
        onClick={() => void handleHarvestAll()}
        disabled={disabled}
        className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-tide-500 text-abyss-950 hover:bg-tide-400 disabled:bg-abyss-700 disabled:text-foam-500 disabled:cursor-not-allowed transition-colors shrink-0"
      >
        {buttonLabel}
      </button>
    </div>
  );
}

function FarmStat({
  icon: Icon,
  label,
  value,
  accent,
}: {
  icon: typeof Sprout;
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div className="p-3 rounded-xl border border-abyss-700 bg-abyss-850">
      <div className="flex items-center gap-1.5 text-[11px] text-foam-500">
        <Icon size={12} />
        {label}
      </div>
      <div className={`text-base font-semibold mt-1 tabular-nums truncate ${accent ? 'text-tide-400' : 'text-foam-100'}`}>
        {value}
      </div>
    </div>
  );
}

function FarmRow({
  pool,
  stats,
  isSelected,
  onSelect,
}: {
  pool: MarketPair;
  stats: FarmStats;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const { stakedAmount, pendingReward } = useFarmPoolData(pool);
  const apr = stats.aprOf(pool.id);

  return (
    <button
      onClick={onSelect}
      className={`w-full text-left p-4 transition-colors ${isSelected ? 'bg-tide-400/10' : 'hover:bg-abyss-800/60'}`}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex -space-x-1.5">
            <TokenIcon symbol={pool.base} size={20} />
            <TokenIcon symbol={pool.quote} size={20} />
          </div>
          <span className="text-sm font-medium text-foam-100 truncate">
            {pool.base}/{pool.quote} LP
          </span>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] text-foam-500">APR</div>
          <div className="text-sm font-semibold text-tide-400 tabular-nums">
            {fmtApr(apr)}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-foam-500">
        <span>TVL: {fmtUsd(stats.tvlOf(pool.id))}</span>
        <span className="text-right">스테이킹: {fmt(stakedAmount, 2)}</span>
        {pendingReward !== undefined && pendingReward > 0n && (
          <span className="col-span-2 text-tide-400">미수령: {fmt(pendingReward, 6)} PLG</span>
        )}
      </div>
    </button>
  );
}

function StakePanel({ pool, stats }: { pool: MarketPair; stats: FarmStats }) {
  const { address, isConnected, chainId } = useAccount();
  const readChainId = chainId ?? TARGET_CHAIN.id;
  const onCorrectChain = chainId === TARGET_CHAIN.id;
  const apr = stats.aprOf(pool.id);
  const tvl = stats.tvlOf(pool.id);

  const {
    deployment,
    pairAddress,
    pid,
    walletLpBalance,
    allowance,
    stakedAmount,
    pendingReward,
    refetchAll,
  } = useFarmPoolData(pool);

  const [mode, setMode] = useState<'stake' | 'unstake'>('stake');
  const [amount, setAmount] = useState('');
  const [txError, setTxError] = useState<string | null>(null);

  const amountWei = safeParse(amount);
  const needsApproval = mode === 'stake' && amountWei !== null && (allowance === undefined || allowance < amountWei);

  const { writeContract: writeApprove, data: approveHash, isPending: isApprovePending } = useWriteContract();
  const { isLoading: isApproveConfirming, isSuccess: isApproveConfirmed, error: approveReceiptError, refetch: retryApproveReceipt } = useWaitForTransactionReceipt({ hash: approveHash, timeout: 300_000 });

  const { writeContract: writeAction, data: actionHash, isPending: isActionPending, reset: resetAction } = useWriteContract();
  const { isLoading: isActionConfirming, isSuccess: isActionConfirmed, error: actionReceiptError, refetch: retryActionReceipt } = useWaitForTransactionReceipt({ hash: actionHash, timeout: 300_000 });

  const {
    writeContract: writeHarvest,
    data: harvestHash,
    isPending: isHarvestPending,
    reset: resetHarvest,
  } = useWriteContract();
  const { isLoading: isHarvestConfirming, isSuccess: isHarvestConfirmed, error: harvestReceiptError, refetch: retryHarvestReceipt } = useWaitForTransactionReceipt({ hash: harvestHash, timeout: 300_000 });

  useEffect(() => {
    if (isApproveConfirmed) refetchAll();
  }, [isApproveConfirmed]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isActionConfirmed && actionHash && address) {
      logTransaction({
        type: mode === 'stake' ? 'farmDeposit' : 'farmWithdraw',
        hash: actionHash,
        account: address,
        chainId: readChainId,
        details: { pool: `${pool.base}-${pool.quote}`, amount },
      });
      setAmount('');
      refetchAll();
      resetAction();
    }
  }, [isActionConfirmed]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isHarvestConfirmed && harvestHash && address) {
      logTransaction({
        type: 'harvest',
        hash: harvestHash,
        account: address,
        chainId: readChainId,
        details: { pool: `${pool.base}-${pool.quote}` },
      });
      refetchAll();
      resetHarvest();
    }
  }, [isHarvestConfirmed]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApprove = () => {
    if (!pairAddress || !deployment?.farm) return;
    setTxError(null);
    writeApprove(
      { address: pairAddress, abi: ERC20_ABI, functionName: 'approve', args: [deployment.farm, 2n ** 256n - 1n] },
      { onError: (err) => setTxError(err.message) },
    );
  };

  const handleAction = () => {
    if (!amountWei || !deployment?.farm || pid === undefined) return;
    setTxError(null);
    writeAction(
      {
        address: deployment.farm,
        abi: FARM_ABI,
        functionName: mode === 'stake' ? 'deposit' : 'withdraw',
        args: [BigInt(pid), amountWei],
      },
      { onError: (err) => setTxError(err.message) },
    );
  };

  const handleHarvest = () => {
    if (!deployment?.farm || pid === undefined) return;
    setTxError(null);
    writeHarvest(
      { address: deployment.farm, abi: FARM_ABI, functionName: 'deposit', args: [BigInt(pid), 0n] },
      { onError: (err) => setTxError(err.message) },
    );
  };

  const isPending =
    isApprovePending || isApproveConfirming || isActionPending || isActionConfirming || isHarvestPending || isHarvestConfirming;

  const maxAmount = mode === 'stake' ? walletLpBalance : stakedAmount;

  let buttonLabel = 'Enter an amount';
  let buttonAction: (() => void) | undefined;
  let buttonDisabled = true;

  if (!isConnected) {
    buttonLabel = 'Connect wallet to farm';
  } else if (!onCorrectChain) {
    buttonLabel = 'Switch network';
  } else if (amountWei) {
    if (needsApproval) {
      buttonLabel = 'Approve LP tokens';
      buttonAction = handleApprove;
      buttonDisabled = isPending;
    } else {
      buttonLabel = mode === 'stake' ? 'Stake' : 'Unstake';
      buttonAction = handleAction;
      buttonDisabled = isPending;
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="flex -space-x-1.5">
            <TokenIcon symbol={pool.base} size={24} />
            <TokenIcon symbol={pool.quote} size={24} />
          </div>
          <span className="text-lg font-semibold text-foam-100">
            {pool.base}/{pool.quote} LP
          </span>
        </div>
        <div className="flex gap-4 text-right">
          <div>
            <div className="text-[10px] text-foam-500">APR</div>
            <div className="text-sm font-semibold text-tide-400 tabular-nums">
              {fmtApr(apr)}
            </div>
          </div>
          <div>
            <div className="text-[10px] text-foam-500">풀 TVL</div>
            <div className="text-sm font-semibold text-foam-100 tabular-nums">{fmtUsd(tvl)}</div>
          </div>
        </div>
      </div>

      <div className="p-4 rounded-xl border border-tide-400/30 bg-tide-400/5 flex items-center justify-between">
        <div>
          <div className="text-xs text-foam-500 flex items-center gap-1.5">
            미수령 PLG 보상
            {pendingReward !== undefined && pendingReward > 0n && (
              <span className="w-1.5 h-1.5 rounded-full bg-tide-400 animate-pulse" />
            )}
          </div>
          <div className="text-2xl font-semibold text-tide-400 tabular-nums">{fmt(pendingReward, 6)}</div>
        </div>
        <button
          onClick={handleHarvest}
          disabled={!pendingReward || pendingReward === 0n || isPending}
          className="px-4 py-2 rounded-lg text-sm font-semibold bg-tide-500 text-abyss-950 hover:bg-tide-400 disabled:bg-abyss-700 disabled:text-foam-500 disabled:cursor-not-allowed transition-colors"
        >
          수확
        </button>
      </div>

      <div className="flex gap-1 p-1 rounded-lg bg-abyss-850 border border-abyss-700 w-fit">
        {(['stake', 'unstake'] as const).map((m) => (
          <button
            key={m}
            onClick={() => {
              setMode(m);
              setAmount('');
            }}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
              mode === m ? 'bg-tide-500 text-abyss-950' : 'text-foam-300 hover:text-foam-100'
            }`}
          >
            {m === 'stake' ? 'Stake' : 'Unstake'}
          </button>
        ))}
      </div>

      <div className="p-3 rounded-xl border border-abyss-700 bg-abyss-850">
        <div className="flex items-center justify-between text-xs text-foam-500 mb-2">
          <span>{mode === 'stake' ? 'Wallet LP balance' : 'Staked LP balance'}</span>
          <button
            onClick={() => maxAmount && setAmount(formatUnits(maxAmount, DECIMALS))}
            className="text-tide-400 hover:text-tide-300 font-medium"
          >
            MAX: {fmt(maxAmount, 6)}
          </button>
        </div>
        <input
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
          placeholder="0.0"
          className="w-full bg-transparent text-2xl font-medium text-foam-100 outline-none placeholder:text-foam-700 tabular-nums"
        />
      </div>

      <button
        onClick={buttonAction}
        disabled={buttonDisabled}
        className="w-full py-3 rounded-xl font-semibold text-sm transition-colors disabled:bg-abyss-700 disabled:text-foam-500 disabled:cursor-not-allowed bg-tide-500 text-abyss-950 hover:bg-tide-400"
      >
        {buttonLabel}
      </button>

      {txError && <div className="px-3 py-2 rounded-md bg-coral-500/15 text-coral-400 text-xs break-words">{txError}</div>}
      <TransactionTimeout attempts={[
        { error: approveReceiptError, hash: approveHash, retry: retryApproveReceipt },
        { error: actionReceiptError, hash: actionHash, retry: retryActionReceipt },
        { error: harvestReceiptError, hash: harvestHash, retry: retryHarvestReceipt },
      ]} />
      {isActionConfirmed && !txError && (
        <div className="px-3 py-2 rounded-md bg-tide-400/10 text-tide-400 text-xs">
          {mode === 'stake' ? 'Staked' : 'Unstaked'} on-chain.
        </div>
      )}
      {isHarvestConfirmed && !txError && (
        <div className="px-3 py-2 rounded-md bg-tide-400/10 text-tide-400 text-xs">Rewards harvested.</div>
      )}
    </div>
  );
}
